// Personel listesi — çerçevesiz tablo (DataTableCard variant="flat").
// Satırın tamamı tıklanır ve personel drawer'ını açar; "İşlemler" sütunu yok.
// Kurallar: docs/design-principles.md §3

import React from "react";
import { Avatar, Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, alpha } from "@mui/material";
import { Star as StarIcon } from "@mui/icons-material";
import { FaUsers } from "react-icons/fa";
import { DataTableCard } from "../../../../components/organisms";
import { formatPhoneDisplay } from "../../../../components/atoms";
import {
  ClickableRow,
  Dash,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  SkeletonRows,
  SortHead,
  StatusPill,
  cellSx,
  mainText,
  subText,
  ROWS_PER_PAGE_OPTIONS,
} from "../../../../components/organisms/ClinicRecordList/recordTableParts";
import { avatarTone, fullName, initialsOf, maskTc } from "../../../Secretary/PatientsPage/patientListFormat";
import { COLORS } from "../../../../styles/uiConstants";
import { statusToneOf } from "./staffFormat";

const COLS = 5;

export default function StaffTable({
  rows,
  totalCount,
  isLoading,
  managerId,
  roleNameOf,
  palette,
  sort,
  dir,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onOpen,
  empty,
}) {
  return (
    <DataTableCard
      loading={Boolean(isLoading)}
      variant="flat"
      empty={!isLoading && rows.length === 0}
      emptyIcon={<FaUsers />}
      emptyTitle={empty.title}
      emptyMessage={empty.message}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <SortHead field="name" label="Personel" sort={sort} dir={dir} onSort={onSort} sx={{ ...EDGE_L, width: "30%" }} />
            <SortHead field="role" label="Rol" sort={sort} dir={dir} onSort={onSort} sx={{ width: "18%" }} />
            <PlainHead sx={{ width: "22%" }}>E-posta</PlainHead>
            <PlainHead sx={{ width: "16%" }}>Telefon</PlainHead>
            <SortHead field="status" label="Durum" sort={sort} dir={dir} onSort={onSort} sx={{ ...EDGE_R, width: "14%" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <SkeletonRows rows={Math.min(8, rowsPerPage)} cols={COLS} />
          ) : rows.length ? (
            rows.map((u) => (
              <StaffRow
                key={u._id}
                user={u}
                isManager={Boolean(managerId) && u._id === managerId}
                roleName={roleNameOf(u)}
                palette={palette}
                onOpen={onOpen}
              />
            ))
          ) : (
            <FillerRow cols={COLS} />
          )}
        </TableBody>
      </Table>
    </DataTableCard>
  );
}

function StaffRow({ user, isManager, roleName, palette, onOpen }) {
  const tone = avatarTone(user);
  const meta = [user.gender, user.tcNo ? `TC ${maskTc(user.tcNo)}` : null].filter(Boolean).join(" · ");
  const phone = formatPhoneDisplay(user.tellNo);

  return (
    <ClickableRow palette={palette} onClick={() => onOpen(user)}>
      {/* Personel */}
      <TableCell sx={{ ...cellSx, ...EDGE_L }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar sx={{ width: 34, height: 34, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
            {initialsOf(user)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ ...mainText, fontWeight: 600 }} noWrap>
                {fullName(user)}
              </Typography>
              {isManager ? (
                <Tooltip title="Klinik yöneticisi" arrow placement="top">
                  <StarIcon sx={{ fontSize: 15, color: COLORS.warning, flexShrink: 0 }} aria-label="Klinik yöneticisi" />
                </Tooltip>
              ) : null}
            </Box>
            {meta ? (
              <Typography sx={subText} noWrap>
                {meta}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      {/* Rol */}
      <TableCell sx={cellSx}>
        <StatusPill label={roleName} tone="primary" palette={palette} />
      </TableCell>

      {/* E-posta */}
      <TableCell sx={cellSx}>
        {user.email ? (
          <Tooltip title={user.email} arrow placement="top">
            <Typography sx={{ ...mainText, pr: 2 }} noWrap>
              {user.email}
            </Typography>
          </Tooltip>
        ) : (
          <Dash />
        )}
      </TableCell>

      {/* Telefon */}
      <TableCell sx={cellSx}>
        {phone ? (
          <Typography sx={{ ...mainText, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{phone}</Typography>
        ) : (
          <Dash />
        )}
      </TableCell>

      {/* Durum */}
      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
        {user.accountStatus ? <StatusPill label={user.accountStatus} tone={statusToneOf(user.accountStatus)} /> : <Dash />}
      </TableCell>
    </ClickableRow>
  );
}
